'use client';

import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';

interface IncomeStatement {
  ingresos: { subtotal: number; items: any[] };
  gastos: { subtotal: number; items: any[] };
  operating_result: number;
  net_result: number;
}

interface BalanceSheet {
  assets: { subtotal: number; current: { subtotal: number; items: any[] }; noncurrent: { subtotal: number; items: any[] }; items: any[] };
  liabilities: { subtotal: number; current: { subtotal: number; items: any[] }; noncurrent: { subtotal: number; items: any[] }; items: any[] };
  equity: { subtotal: number; items: any[] };
  is_balanced: boolean;
}

interface CashFlow {
  operating_activities: number;
  investing_activities: number;
  financing_activities: number;
  net_cash_flow: number;
}

interface FinancialRatios {
  liquidity: { current_ratio: number; quick_ratio: number };
  solvency: { debt_to_equity: number; debt_ratio: number; equity_ratio: number };
  profitability: { profit_margin: number; roa: number; roe: number };
}

export default function FinancialStatements() {
  const [activeTab, setActiveTab] = useState<'income' | 'balance' | 'cash' | 'ratios'>('income');
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  const [incomeStatement, setIncomeStatement] = useState<IncomeStatement | null>(null);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheet | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlow | null>(null);
  const [ratios, setRatios] = useState<FinancialRatios | null>(null);

  const loadIncomeStatement = async () => {
    setLoading(true);
    try {
      const response: any = await api.get('/comptabilitat/financial-statements/income-statement', {
        params: { start_date: startDate, end_date: endDate },
      });
      if (response.success) {
        setIncomeStatement(response.data);
      }
    } catch (error) {
      console.error('[v0] Error:', error);
      alert('Error en carregar l\'Estat de Resultats');
    }
    setLoading(false);
  };

  const loadBalanceSheet = async () => {
    setLoading(true);
    try {
      const response: any = await api.get('/comptabilitat/financial-statements/balance-sheet', {
        params: { as_of_date: asOfDate },
      });
      if (response.success) {
        setBalanceSheet(response.data);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error en carregar el Balanç General');
    }
    setLoading(false);
  };

  const loadCashFlow = async () => {
    setLoading(true);
    try {
      const response: any = await api.get('/comptabilitat/financial-statements/cash-flow', {
        params: { start_date: startDate, end_date: endDate },
      });
      if (response.success) {
        setCashFlow(response.data);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error en carregar el Flux de Caixa');
    }
    setLoading(false);
  };

  const loadRatios = async () => {
    setLoading(true);
    try {
      const response: any = await api.get('/comptabilitat/financial-statements/ratios', {
        params: { as_of_date: asOfDate, end_date: endDate },
      });
      if (response.success) {
        setRatios(response.data);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error en carregar els Ràtios');
    }
    setLoading(false);
  };

  const handleTabChange = (tab: 'income' | 'balance' | 'cash' | 'ratios') => {
    setActiveTab(tab);
    if (tab === 'income') loadIncomeStatement();
    else if (tab === 'balance') loadBalanceSheet();
    else if (tab === 'cash') loadCashFlow();
    else if (tab === 'ratios') loadRatios();
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Estats Financers</h1>
      </div>

      <Card className="p-6 bg-blue-50">
        <h3 className="font-bold mb-4">Filtre de Data</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Data Inici</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Data Final</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Data Referència (Balanç)</label>
            <Input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </div>
        </div>
        <Button
          className="mt-4 bg-blue-600 hover:bg-gray-700"
          onClick={() => {}}
        >
          Actualitzar
        </Button>
      </Card>

      <div className="flex gap-2 border-b overflow-x-auto">
        <Button
          variant={activeTab === 'income' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('income')}
        >
          Estat de Resultats
        </Button>
        <Button
          variant={activeTab === 'balance' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('balance')}
        >
          Balanç General
        </Button>
        <Button
          variant={activeTab === 'cash' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('cash')}
        >
          Flux de Caixa
        </Button>
        <Button
          variant={activeTab === 'ratios' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('ratios')}
        >
          Ràtios Financers
        </Button>
      </div>

      {activeTab === 'income' && (
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-4">Estat de Resultats</h2>
          {incomeStatement ? (
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-sm mb-3 text-green-700">INGRESSOS</h3>
                {incomeStatement.ingresos.items.map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between p-2 border-b">
                    <span>{item.account_code} - {item.account_name}</span>
                    <span className="font-mono">{item.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between p-2 font-bold bg-green-50">
                  <span>Total Ingressos</span>
                  <span className="font-mono">{incomeStatement.ingresos.subtotal.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-sm mb-3 text-red-700">DESPESES</h3>
                {incomeStatement.gastos.items.map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between p-2 border-b">
                    <span>{item.account_code} - {item.account_name}</span>
                    <span className="font-mono">{item.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between p-2 font-bold bg-red-50">
                  <span>Total Despeses</span>
                  <span className="font-mono">{incomeStatement.gastos.subtotal.toFixed(2)}</span>
                </div>
              </div>

              <div className={`p-4 rounded font-bold text-sm flex justify-between ${incomeStatement.net_result >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                <span>RESULTAT NET</span>
                <span className="font-mono">{incomeStatement.net_result.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <Button onClick={loadIncomeStatement} disabled={loading}>
              {loading ? 'Carregant...' : 'Carregar Estat de Resultats'}
            </Button>
          )}
        </Card>
      )}

      {activeTab === 'balance' && (
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-4">Balanç General</h2>
          {balanceSheet ? (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-sm mb-3 text-blue-700">ACTIUS</h3>

                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-600 mb-2">Actius Corrents</h4>
                  {balanceSheet.assets.current.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between p-2 text-sm border-b">
                      <span>{item.account_code}</span>
                      <span className="font-mono">{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 font-semibold bg-blue-50">
                    <span>Subtotal</span>
                    <span className="font-mono">{balanceSheet.assets.current.subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-600 mb-2">Actius No Corrents</h4>
                  {balanceSheet.assets.noncurrent.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between p-2 text-sm border-b">
                      <span>{item.account_code}</span>
                      <span className="font-mono">{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 font-semibold bg-blue-50">
                    <span>Subtotal</span>
                    <span className="font-mono">{balanceSheet.assets.noncurrent.subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between p-2 font-bold bg-blue-100 rounded">
                  <span>TOTAL ACTIUS</span>
                  <span className="font-mono">{balanceSheet.assets.subtotal.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-sm mb-3 text-purple-700">PASSIUS I PATRIMONI</h3>

                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-600 mb-2">Passius Corrents</h4>
                  {balanceSheet.liabilities.current.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between p-2 text-sm border-b">
                      <span>{item.account_code}</span>
                      <span className="font-mono">{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 font-semibold bg-purple-50">
                    <span>Subtotal</span>
                    <span className="font-mono">{balanceSheet.liabilities.current.subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-600 mb-2">Passius No Corrents</h4>
                  {balanceSheet.liabilities.noncurrent.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between p-2 text-sm border-b">
                      <span>{item.account_code}</span>
                      <span className="font-mono">{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 font-semibold bg-purple-50">
                    <span>Subtotal</span>
                    <span className="font-mono">{balanceSheet.liabilities.noncurrent.subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-600 mb-2">Patrimoni</h4>
                  {balanceSheet.equity.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between p-2 text-sm border-b">
                      <span>{item.account_code}</span>
                      <span className="font-mono">{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-2 font-semibold bg-purple-50">
                    <span>Subtotal</span>
                    <span className="font-mono">{balanceSheet.equity.subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className={`flex justify-between p-2 font-bold rounded ${balanceSheet.is_balanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <span>TOTAL PAS + PATR</span>
                  <span className="font-mono">{(balanceSheet.liabilities.subtotal + balanceSheet.equity.subtotal).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ) : (
            <Button onClick={loadBalanceSheet} disabled={loading}>
              {loading ? 'Carregant...' : 'Carregar Balanç General'}
            </Button>
          )}
        </Card>
      )}

      {activeTab === 'cash' && (
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-4">Flux de Caixa</h2>
          {cashFlow ? (
            <div className="space-y-4">
              <div className="p-4 border rounded">
                <div className="flex justify-between">
                  <span>Flux d&apos;Activitats Operacionals</span>
                  <span className="font-mono font-bold">{cashFlow.operating_activities.toFixed(2)}</span>
                </div>
              </div>
              <div className="p-4 border rounded">
                <div className="flex justify-between">
                  <span>Flux d&apos;Activitats d&apos;Inversió</span>
                  <span className="font-mono font-bold">{cashFlow.investing_activities.toFixed(2)}</span>
                </div>
              </div>
              <div className="p-4 border rounded">
                <div className="flex justify-between">
                  <span>Flux d&apos;Activitats de Finançament</span>
                  <span className="font-mono font-bold">{cashFlow.financing_activities.toFixed(2)}</span>
                </div>
              </div>
              <div className={`p-4 rounded font-bold text-sm flex justify-between ${cashFlow.net_cash_flow >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                <span>Flux de Caixa Net</span>
                <span className="font-mono">{cashFlow.net_cash_flow.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <Button onClick={loadCashFlow} disabled={loading}>
              {loading ? 'Carregant...' : 'Carregar Flux de Caixa'}
            </Button>
          )}
        </Card>
      )}

      {activeTab === 'ratios' && (
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-4">Ràtios Financers</h2>
          {ratios ? (
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h3 className="font-bold text-sm mb-3 text-blue-700">Liquiditat</h3>
                <div className="space-y-2">
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Ràtio Corrent</div>
                    <div className="font-mono font-bold text-sm">{ratios.liquidity.current_ratio.toFixed(2)}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Ràtio Ràpida</div>
                    <div className="font-mono font-bold text-sm">{ratios.liquidity.quick_ratio.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-sm mb-3 text-purple-700">Solvència</h3>
                <div className="space-y-2">
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Deute/Patrimoni</div>
                    <div className="font-mono font-bold text-sm">{ratios.solvency.debt_to_equity.toFixed(2)}</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Ràtio Deute</div>
                    <div className="font-mono font-bold text-sm">{(ratios.solvency.debt_ratio * 100).toFixed(1)}%</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Ràtio Patrimoni</div>
                    <div className="font-mono font-bold text-sm">{(ratios.solvency.equity_ratio * 100).toFixed(1)}%</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-sm mb-3 text-green-700">Rendibilitat</h3>
                <div className="space-y-2">
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">Marge de Benefici</div>
                    <div className="font-mono font-bold text-sm">{ratios.profitability.profit_margin.toFixed(1)}%</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">ROA</div>
                    <div className="font-mono font-bold text-sm">{ratios.profitability.roa.toFixed(1)}%</div>
                  </div>
                  <div className="p-3 border rounded">
                    <div className="text-sm text-gray-600">ROE</div>
                    <div className="font-mono font-bold text-sm">{ratios.profitability.roe.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Button onClick={loadRatios} disabled={loading}>
              {loading ? 'Carregant...' : 'Carregar Ràtios'}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
